/**
 * AK-938 — создать / изменить схему опыта (low-fi 2·Б): общие данные, факторы
 * (мультивыбор), варианты с препаратами и дозами, шаблон осмотров.
 *
 * «Сохранить схему» — действующая (по ней можно закладывать; роли проверяются);
 * «Сохранить черновик» — можно с неполным составом. Опыты, уже заложенные по
 * схеме, при правке не меняются: у них своя копия (ADR-086 И-3).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stack } from '@/components/ui/stack';
import { VariantsEditor } from '@/components/science/VariantsEditor';
import { ApiError } from '@/lib/api';
import { useActiveSeason } from '@/lib/season/SeasonContext';
import { toast } from '@/lib/toast';
import { useSaveTrialScheme, useTrialScheme } from '@/lib/science/api';
import { CHECK_KIND_LABELS, FACTOR_LABELS, FACTOR_ORDER } from '@/lib/science/labels';
import {
  buildSchemeBody,
  draftKey,
  emptySchemeDraft,
  schemeToDraft,
  type DraftErrors,
  type SchemeDraft,
} from '@/lib/science/schemeDraft';
import { useScienceSeasonLensHost } from '@/lib/science/seasonLens';
import type { CheckValueKind } from '@/lib/science/types';

function yearsAround(year: number): number[] {
  return [year - 1, year, year + 1, year + 2];
}

export function TrialSchemeFormPage({ schemeId }: { schemeId?: string }) {
  useScienceSeasonLensHost();
  const navigate = useNavigate();
  const { season } = useActiveSeason();
  const existing = useTrialScheme(schemeId ?? null);
  const save = useSaveTrialScheme(schemeId ?? null);
  const baseYear = season?.year ?? new Date().getFullYear();
  const [draft, setDraft] = useState<SchemeDraft | null>(schemeId ? null : emptySchemeDraft(baseYear));
  const [errors, setErrors] = useState<DraftErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (schemeId && existing.data && !draft) setDraft(schemeToDraft(existing.data));
  }, [schemeId, existing.data, draft]);

  function update(next: Partial<SchemeDraft>) {
    setDraft((d) => (d ? { ...d, ...next } : d));
    setErrors({});
    setSubmitError(null);
  }

  async function submit(status: 'draft' | 'active') {
    if (!draft) return;
    const built = buildSchemeBody(draft, status);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    try {
      const saved = await save.mutateAsync(built.body);
      toast.success(status === 'active' ? 'Схема сохранена' : 'Черновик сохранён');
      void navigate({ to: '/science/schemes/$schemeId', params: { schemeId: saved.id } });
    } catch (e) {
      setSubmitError(e instanceof ApiError ? e.detail : 'Не удалось сохранить схему');
    }
  }

  const years = yearsAround(baseYear);
  if (draft && !years.includes(draft.seasonYear)) years.push(draft.seasonYear);

  return (
    <AppShell>
      <Stack gap={6}>
        <PageHeader
          title={schemeId ? 'Изменить схему опыта' : 'Новая схема опыта'}
          description="По схеме потом закладывают опыты на поля — сколько угодно раз"
          breadcrumbs={[
            { label: 'Наука', to: '/science' },
            { label: 'Схемы', to: '/science', search: { tab: 'schemes' } },
            { label: schemeId ? 'Изменить' : 'Новая схема' },
          ]}
          actions={
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => window.history.back()}>
                Отмена
              </Button>
              <Button
                variant="outline"
                disabled={!draft || save.isPending}
                onClick={() => void submit('draft')}
              >
                Сохранить черновик
              </Button>
              <Button disabled={!draft || save.isPending} onClick={() => void submit('active')}>
                Сохранить схему
              </Button>
            </div>
          }
        />

        {!draft ? (
          existing.isError ? (
            <Alert variant="destructive">
              <AlertDescription>Схему открыть не удалось: {existing.error.message}</AlertDescription>
            </Alert>
          ) : (
            <Skeleton className="h-64 w-full" />
          )
        ) : (
          <>
            {submitError && (
              <Alert variant="destructive">
                <AlertDescription role="alert">{submitError}</AlertDescription>
              </Alert>
            )}
            {schemeId && (existing.data?.trials_count ?? 0) > 0 && (
              <Alert>
                <AlertDescription>
                  По схеме уже заложены опыты — они не изменятся (у каждого своя копия). Новая версия
                  пойдёт в следующие опыты.
                </AlertDescription>
              </Alert>
            )}
            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Общие данные</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 gap-4 p-4 pt-0 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="scheme-name">Название</Label>
                  <Input
                    id="scheme-name"
                    value={draft.name}
                    aria-invalid={!!errors.name || undefined}
                    className={errors.name ? 'border-destructive' : undefined}
                    onChange={(e) => update({ name: e.target.value })}
                    placeholder="Гербицид против двудольных в пшенице"
                  />
                  {errors.name && (
                    <p role="alert" className="text-xs text-destructive">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Label>Сезон</Label>
                  <Select
                    value={String(draft.seasonYear)}
                    onValueChange={(v) => update({ seasonYear: Number(v) })}
                  >
                    <SelectTrigger aria-label="Сезон">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-2 md:col-span-2">
                  <Label htmlFor="scheme-comment">Комментарий</Label>
                  <Input
                    id="scheme-comment"
                    value={draft.comment}
                    onChange={(e) => update({ comment: e.target.value })}
                    placeholder="например: показать клиенту против эталона"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Факторы</CardTitle>
                <p className="text-sm text-muted-foreground">можно несколько</p>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-4 p-4 pt-0">
                {FACTOR_ORDER.map((f) => {
                  const on = draft.factors.includes(f);
                  return (
                    <div key={f} className="flex items-center gap-2">
                      <Checkbox
                        id={`factor-${f}`}
                        checked={on}
                        onCheckedChange={(v) =>
                          update({
                            factors: v === true
                              ? FACTOR_ORDER.filter((x) => x === f || draft.factors.includes(x))
                              : draft.factors.filter((x) => x !== f),
                          })
                        }
                      />
                      <Label htmlFor={`factor-${f}`} className="text-sm font-normal">
                        {FACTOR_LABELS[f]}
                      </Label>
                    </div>
                  );
                })}
                {errors.factors && (
                  <p role="alert" className="w-full text-xs text-destructive">
                    {errors.factors}
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Варианты</CardTitle>
                <p className="text-sm text-muted-foreground">
                  у каждого варианта — что применяем по выбранным факторам
                </p>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <VariantsEditor
                  variants={draft.variants}
                  errors={errors}
                  onChange={(variants) => update({ variants })}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="p-4 pb-3">
                <CardTitle className="text-base">Шаблон осмотров</CardTitle>
                <p className="text-sm text-muted-foreground">
                  что смотрим на каждом осмотре и как записываем
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-3 p-4 pt-0">
                {draft.checkItems.map((c) => (
                  <div key={c.key} className="flex flex-col gap-1">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,2fr)_auto] md:items-center">
                      <Input
                        aria-label="Что смотрим"
                        value={c.label}
                        placeholder="Засорённость"
                        aria-invalid={!!errors[c.key] || undefined}
                        onChange={(e) =>
                          update({
                            checkItems: draft.checkItems.map((x) =>
                              x.key === c.key ? { ...x, label: e.target.value } : x,
                            ),
                          })
                        }
                      />
                      <Select
                        value={c.value_kind}
                        onValueChange={(v) =>
                          update({
                            checkItems: draft.checkItems.map((x) =>
                              x.key === c.key ? { ...x, value_kind: v as CheckValueKind } : x,
                            ),
                          })
                        }
                      >
                        <SelectTrigger aria-label="Как записываем">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(CHECK_KIND_LABELS) as CheckValueKind[]).map((k) => (
                            <SelectItem key={k} value={k}>
                              {CHECK_KIND_LABELS[k]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        aria-label="Варианты ответа"
                        value={c.choicesText}
                        disabled={c.value_kind !== 'choice'}
                        placeholder={c.value_kind === 'choice' ? 'нет, слабо, сильно' : ''}
                        onChange={(e) =>
                          update({
                            checkItems: draft.checkItems.map((x) =>
                              x.key === c.key ? { ...x, choicesText: e.target.value } : x,
                            ),
                          })
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Убрать показатель"
                        onClick={() =>
                          update({ checkItems: draft.checkItems.filter((x) => x.key !== c.key) })
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors[c.key] && (
                      <p role="alert" className="text-xs text-destructive">
                        {errors[c.key]}
                      </p>
                    )}
                  </div>
                ))}
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      update({
                        checkItems: [
                          ...draft.checkItems,
                          { key: draftKey('c'), label: '', value_kind: 'number', choicesText: '' },
                        ],
                      })
                    }
                  >
                    <Plus className="h-4 w-4" />
                    Добавить показатель
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </Stack>
    </AppShell>
  );
}
